/**
 * PremiumCallback — Post-checkout redirect handler.
 * Route: /premium/callback
 *
 * When PayFast redirects back with ?status=success, calls confirmWebPayment
 * to activate premium immediately (the ITN webhook is the source of truth
 * but may be delayed).
 */

import React, { useEffect, useRef, useState } from 'react';
import { capture } from '../services/posthog';
import { MonetizationEvents } from '../services/monetizationEvents';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader } from 'react-feather';
import { useAuth } from '../hooks/useAuth';
import { confirmWebPaymentEurW } from '../firebaseConfig';
import SEO from '../components/SEO';
import '../styles/modern.css';
import '../styles/premium.css';

type CallbackState = 'verifying' | 'success' | 'failed' | 'pending';

export default function PremiumCallback() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status'); // 'success' or 'cancelled'
  const [state, setState] = useState<CallbackState>(
    status === 'cancelled' ? 'failed' : 'verifying',
  );

  /**
   * PPL-351 — PayFast sent them back without paying.
   *
   * The only client-observable end of a web checkout: the server sees
   * `checkout_started` and then, for an abandonment, nothing at all. Without this
   * the funnel's last two steps are indistinguishable from a user still deciding.
   * Once per mount, not once per render.
   */
  const abandonLogged = useRef(false);
  useEffect(() => {
    if (status !== 'cancelled' || abandonLogged.current) return;
    abandonLogged.current = true;
    capture(MonetizationEvents.CHECKOUT_ABANDONED, {
      billing_cycle: sessionStorage.getItem('pplkpr_checkout_plan'),
      step: 'payfast_redirect',
    });
  }, [status]);

  useEffect(() => {
    if (status === 'cancelled' || !user?.uid) return;

    // Premium is granted server-side by the PayFast ITN webhook (the source of
    // truth, which server-validates the payment). confirmWebPaymentEurW is
    // read-only — poll it until activation lands.
    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 45; // ~90s at 2s intervals (ITNs — esp. sandbox — can lag)

    const poll = async () => {
      try {
        const res: any = await confirmWebPaymentEurW({});
        if (cancelled) return;
        if (res?.data?.success) {
          setState('success');
          return;
        }
      } catch (err) {
        console.error('Failed to confirm payment:', err);
      }
      if (cancelled) return;
      attempts += 1;
      if (attempts >= maxAttempts) {
        setState('pending');
        return;
      }
      setTimeout(poll, 2000);
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [user?.uid, status]);

  return (
    <div className="premium-page-wrap">
      <div className="premium-content" style={{ maxWidth: 600 }}>
        <SEO title="Subscription | pplkpr" path="/premium/callback" />

        <div className="premium-pricing-card" style={{ padding: '48px 24px', textAlign: 'center', marginTop: 40 }}>
          {state === 'verifying' && (
            <>
              <Loader size={40} className="spin" style={{ color: '#f0abfc', marginBottom: 20 }} />
              <h2 className="premium-section-title" style={{ marginBottom: 8 }}>confirming your subscription...</h2>
              <p style={{ color: 'rgba(232,220,250,0.65)', fontSize: 14 }}>
                this usually takes a few seconds.
              </p>
            </>
          )}

          {state === 'success' && (
            <>
              <div style={{ marginBottom: 16, color: '#f0abfc' }}>
                <CheckCircle size={48} />
              </div>
              <h2 className="premium-section-title" style={{ marginBottom: 8 }}>you're on premium!</h2>
              <p style={{ color: 'rgba(232,220,250,0.65)', fontSize: 14, marginBottom: 24 }}>
                enjoy unlimited access to all pplkpr features.
              </p>
              <a
                href="/f8chat"
                className="premium-cta-btn"
                style={{ textDecoration: 'none', maxWidth: 280, margin: '0 auto' }}
              >
                start exploring
              </a>
            </>
          )}

          {state === 'pending' && (
            <>
              <Loader size={40} className="spin" style={{ color: '#f0abfc', marginBottom: 20 }} />
              <h2 className="premium-section-title" style={{ marginBottom: 8 }}>almost there</h2>
              <p style={{ color: 'rgba(232,220,250,0.65)', fontSize: 14, marginBottom: 24 }}>
                if your payment went through, premium will activate within a minute or two — no need to pay again. you can keep using pplkpr in the meantime.
              </p>
              <a
                href="/f8chat"
                className="premium-cta-btn"
                style={{ textDecoration: 'none', maxWidth: 280, margin: '0 auto' }}
              >
                continue
              </a>
            </>
          )}

          {state === 'failed' && (
            <>
              <XCircle size={48} style={{ color: '#ff6b6b', marginBottom: 16 }} />
              <h2 className="premium-section-title" style={{ marginBottom: 8 }}>
                {status === 'cancelled' ? 'checkout cancelled' : 'something went wrong'}
              </h2>
              <p style={{ color: 'rgba(232,220,250,0.65)', fontSize: 14, marginBottom: 24 }}>
                {status === 'cancelled'
                  ? "no worries — you weren't charged."
                  : "we couldn't confirm your subscription. if you were charged, your premium access will activate shortly."}
              </p>
              <Link
                to="/premium"
                className="premium-cta-btn"
                style={{ textDecoration: 'none', maxWidth: 280, margin: '0 auto' }}
              >
                try again
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
